import React from 'react'
import classnames from 'classnames'

import { AutosizedSection, Link } from '@/libraries/ui'
import { useT } from '@/i18n'

import './Slide.css'
import './slideStyles.scss'

export { SlideContainer, type SlideContainerProps } from './SlideContainer'

export interface SlideProps {
  id: string
  invisible?: boolean // For pre-rendering slides so that autosizing and image loading is done before the slide is shown
  title: string | React.ReactElement
  type?: string
  children?: React.ReactNode
  footer?: string | React.ReactElement
  next?: SlideLink
  navigation?: SlideNavigation
  additionalContent?: React.ReactNode
  slideStyleId?: string | null | undefined
  linkComponent?: LinkComponentType
}

export interface SlideNavigation {
  title: string
  items: SlideLink[]
}
export interface SlideLink {
  id: string
  title: string | React.ReactElement
  url?: string | undefined
  hidden?: boolean
}

export function Slide({ id, invisible, title, type, children, footer, next, navigation, slideStyleId, linkComponent, additionalContent }: SlideProps) {
  const slideClassName = classnames(
    'slide',
    `slide-style-${slideStyleId ?? 'default'}`,
    type && 'slide-type-' + type,
    { invisible },
  )
  return <section className={slideClassName}>
    <h1 className="slide-title">{title}</h1>
    <div className="slide-content">
      <AutosizedSection key={id} className="slide-main-content">
        <div className="slide-content-area">{children}</div>
      </AutosizedSection>
      {footer &&
      <AutosizedSection className="slide-program-footer">
        <div className="slide-content-area">{footer}</div>
      </AutosizedSection>
      }
      {next && <NextSlide next={next} linkComponent={linkComponent} />}
    </div>
    <SlideSidebar
      currentItem={id}
      navigation={navigation}
      linkComponent={linkComponent}
      additionalContent={additionalContent}
    />
  </section>
}

function NextSlide({ next, linkComponent }: { next: SlideLink, linkComponent?: LinkComponentType }) {
  const t = useT('components.slide')
  return <section className="slide-next-slide">
    <div className="slide-content-area">
      {t('afterThis')}:{' '}
      <LinkToSlide {...next} component={linkComponent} />
    </div>
  </section>
}

function SlideSidebar(
  { currentItem, navigation, linkComponent, additionalContent }: {
    currentItem: string
    navigation?: SlideNavigation
    linkComponent?: LinkComponentType
    additionalContent?: React.ReactNode
  },
) {
  if (!navigation) return null

  return <>
    <h2 className="slide-sidebar-title">{navigation?.title}</h2>
    <div className="slide-sidebar-content">
      <AutosizedSection className="slide-navigation">
        <SlideNavigationList currentItem={currentItem} {...navigation} linkComponent={linkComponent} />
      </AutosizedSection>
      {additionalContent && <div className="slide-additional-content">{additionalContent}</div>}
    </div>
  </>
}

interface SlideNavigationListProps extends Pick<SlideNavigation, 'items'> {
  currentItem?: string
  linkComponent?: LinkComponentType
}

export function SlideNavigationList({ items, currentItem, linkComponent }: SlideNavigationListProps) {
  if (!items.length) return null

  return <ul className={classnames('slide-content-area slide-navigation-list', { 'has-current': currentItem !== undefined })}>
    {items.filter(item => item.hidden !== true || item.id === currentItem).map((item) =>
      <li key={item.id} className={item.id === currentItem ? 'current' : undefined}>
        <LinkToSlide {...item} component={linkComponent} />
      </li>,
    )}
  </ul>
}

export type LinkComponentType = React.ComponentType<{
  href: string
  children: React.ReactNode
  className?: string
}> | 'a'

interface LinkToSlideProps extends SlideLink {
  component?: LinkComponentType
}

function LinkToSlide({ title, id, component: LinkComponent }: LinkToSlideProps) {
  if (LinkComponent) {
    return <LinkComponent href={id}>{title}</LinkComponent>
  }
  return <Link unstyled to="." params={{ slideId: id }}>{title}</Link>
}
